'use client';

import type { ComponentProps, MouseEvent } from 'react';
import React, { forwardRef, useCallback } from 'react';
import { sanitizeURL } from '@/utils/domain/route';
import { useNavigationProgressContextSafe } from '@/contexts/NavigationProgressContext';
import { Link } from '@skin-seoul/design-system';
import NextLink from 'next/link';
import { isEmpty } from 'es-toolkit/compat';
import { BridgeApi } from '@skin-seoul/utils';
export type SafeLinkProps = {
  href: string;
} & ComponentProps<typeof Link>;

/**
 * Checks if the click should trigger a client-side navigation.
 */
const shouldTriggerNavigation = (event: MouseEvent<HTMLAnchorElement>, href: string): boolean => {
  // Skip if modifier keys pressed (opens in new tab/window)
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
    return false;
  }

  // Skip if default prevented
  if (event.defaultPrevented) {
    return false;
  }

  // Skip if not primary button click
  if (event.button !== 0) {
    return false;
  }
  try {
    const url = new URL(href, window.location.origin);
    const currentUrl = new URL(window.location.href);

    // External link (different origin)
    if (url.origin !== currentUrl.origin) {
      return false;
    }

    // Same URL - no navigation needed
    if (url.href === currentUrl.href) {
      return false;
    }

    // Hash-only change on same page
    if (url.pathname === currentUrl.pathname && url.search === currentUrl.search && url.hash !== currentUrl.hash) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * NOTE: https://github.com/vercel/next.js/issues/65335#issuecomment-2583401824
 * To navigate to an internal or external route, use the `SafeLink` component.
 * There is an issue that navigating performance is too low, when we navigate to wordpress route with next/link without origin.
 *
 * This component also triggers the page transition loading indicator when clicked.
 *
 * In WebView, product URLs are intercepted and navigated to native ProductDetail screen.
 *
 * @param param0
 * @returns
 */
export const SafeLink = forwardRef<HTMLAnchorElement, SafeLinkProps>(({
  href,
  children,
  onClick,
  ...props
}, ref) => {
  const {
    url
  } = sanitizeURL(href);
  const navigationProgress = useNavigationProgressContextSafe();
  const handleClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    // Call original onClick handler if provided
    onClick?.(event);

    // In WebView, intercept URLs that should navigate to native screens
    if (BridgeApi.isWebview() && BridgeApi.tryNavigateToNativeScreen(url)) {
      event.preventDefault();
      return;
    }

    // Start navigation progress if this is an internal navigation
    if (navigationProgress && shouldTriggerNavigation(event, url)) {
      navigationProgress.startNavigation();
    }
  }, [onClick, navigationProgress, url]);
  return <Link {...isEmpty(props) && {
    unstyled: true
  }} {...props} asChild ref={ref}>
      <NextLink href={url} prefetch={false} onClick={handleClick}>
        {children}
      </NextLink>
    </Link>;
});
SafeLink.displayName = 'SafeLink';